import { IProducts } from "../../type";
import {
  getHeadersWithUniqueVisitorId,
  ShoppingCartItem,
  UserRetailerShoppingCart,
  CartItem,
} from "@brandclub/common-ui";
import { HOST_DOMAIN } from "../../utils/domain";
import { getCommonRequestHeaders } from "../../utils/axios";
import { CartRefusal } from "../../utils/cartValidation";

// Checkout surface migrated to the dedicated api-checkout host (/api-checkout).
const DOMAIN = HOST_DOMAIN + "/api-checkout";

export interface AllUserRetailerShoppingCartsResponse {
  userRetailerShoppingCarts: UserRetailerShoppingCart[];
  products: IProducts;
  status?: string;
  cartItems: CartItem[];
}

export interface UpdateItemsToUserRetailerShoppingCartResponse {
  userRetailerShoppingCart?: UserRetailerShoppingCart;
  userRetailerShoppingCarts?: UserRetailerShoppingCart[];
  status?: string;
}

/** The answer of a cart write. A refusal that carries the server's sentence is a `CartRefusal`. */
const cartWriteAnswer = async (res: Response): Promise<UpdateItemsToUserRetailerShoppingCartResponse> => {
  if (!res.ok) throw new Error("Error updating items to user retailer shopping cart");
  const data = await res.json();
  if (data?.status === "error") {
    const said = data?.errorMessage || data?.message;
    throw said ? new CartRefusal(said) : new Error("Error updating items to user retailer shopping cart");
  }
  return data;
};

export const getActiveCarts = async (
  signal?: AbortSignal
): Promise<AllUserRetailerShoppingCartsResponse> => {
  const headers = {
    ...(await getHeadersWithUniqueVisitorId()),
    ...getCommonRequestHeaders(),
  };
  const res = await fetch(
    DOMAIN + `/retailerCheckout/getAllUserRetailerShoppingCarts`,
    {
      method: "GET",
      headers,
      signal,
    }
  );
  if (res.ok) {
    return await res.json();
  } else {
    throw new Error("Error fetching active carts");
  }
};

export const updateItemsToUserRetailerShoppingCart = async (
  updateItemsToUserRetailerShoppingCartRequest: {
    retailerId: number;
    items: ShoppingCartItem[];
  },
  signal?: AbortSignal
): Promise<UpdateItemsToUserRetailerShoppingCartResponse> => {
  const headers = {
    ...(await getHeadersWithUniqueVisitorId()),
    ...getCommonRequestHeaders(),
  };
  const res = await fetch(
    DOMAIN + `/retailerCheckoutV2/updateItemsToUserRetailerShoppingCart`,
    {
      method: "POST",
      headers,
      signal,

      body: JSON.stringify(updateItemsToUserRetailerShoppingCartRequest),
    }
  );
  return cartWriteAnswer(res);
};

const mapShoppingCartItemsToRequest = (
  items: ShoppingCartItem[]
): {
  brandId: number;
  stacklineSku: string;
  quantity: number;
  retailPrice: number;
  retailerSku: string;
  retailerId: number;
}[] => {
  return items.map((item) => ({
    brandId: item.brandId,
    stacklineSku: item.stacklineSku,
    quantity: item.quantity,
    retailPrice: item.retailPrice,
    retailerSku: item.retailerSku,
    retailerId: item.retailerId,
  }));
};

export const removeItemFromShoppingCart = async (
  {
    retailerId,
    items,
  }: {
    retailerId: number;
    items: ShoppingCartItem[];
  },
  signal?: AbortSignal
): Promise<UpdateItemsToUserRetailerShoppingCartResponse> => {
  const headers = {
    ...(await getHeadersWithUniqueVisitorId()),
    ...getCommonRequestHeaders(),
  };
  const request = {
    retailerId,
    items: mapShoppingCartItemsToRequest(items),
  };

  const res = await fetch(
    DOMAIN + `/retailerCheckoutV2/removeItemsToUserRetailerShoppingCart`,
    {
      method: "POST",
      headers,
      signal,
      body: JSON.stringify(request),
    }
  );
  return cartWriteAnswer(res);
};

/**
 * Write the concierge touch to the shopper's Brandclub bag for one retailer.
 * The checkout reads it back when the tab no longer holds it.
 */
export const stampConciergeTouchOnShoppingCart = async ({
  retailerId,
  conciergeTouchId,
}: {
  retailerId: number;
  conciergeTouchId: string;
}): Promise<void> => {
  const headers = {
    ...(await getHeadersWithUniqueVisitorId()),
    ...getCommonRequestHeaders(),
  };
  const res = await fetch(
    DOMAIN + `/retailerCheckoutV2/stampConciergeTouchOnShoppingCart?retailerId=${retailerId}`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({ conciergeTouchId }),
    }
  );
  if (!res.ok) throw new Error("Error writing the concierge touch to the bag");
};

export const createShareableShoppingCart = async (
  {
    retailerId,
    items,
  }: {
    retailerId: number;
    items: ShoppingCartItem[];
  },
  signal?: AbortSignal
): Promise<{
  shoppingCartId: string;
}> => {
  const headers = {
    ...(await getHeadersWithUniqueVisitorId()),
    ...getCommonRequestHeaders(),
  };
  const request = {
    retailerId,
    items: mapShoppingCartItemsToRequest(items),
  };

  const res = await fetch(
    DOMAIN + `/retailerCheckoutV2/createShareableShoppingCart`,
    {
      method: "POST",
      headers,
      signal,
      body: JSON.stringify(request),
    }
  );
  if (res.ok) {
    return await res.json();
  } else {
    throw new Error("Error creating shareable shopping cart");
  }
};
